import React, { useState } from 'react';
import { Book, Play, Trash2, RefreshCw, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card.js';
import { Badge } from '../../../components/ui/badge.js';
import { Input } from '../../../components/ui/input.js';
import { KnowledgeRunbook } from '../../../stores/useKnowledgeStore.js';

interface RunbooksTabProps {
  searchTerm: string;
  selectedAgent: string;
  selectedType: string;
  items: any[];
  loading: boolean;
  error: string | null;
  onDelete: (itemId: string) => Promise<void>;
  onRefresh: () => void;
}

const RunbooksTab: React.FC<RunbooksTabProps> = ({ searchTerm, selectedAgent, selectedType, items, loading, error, onDelete, onRefresh }) => {
  const [sortBy, setSortBy] = useState<'title' | 'lastUsed' | 'successRate' | 'difficulty'>('title');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const runbooks: KnowledgeRunbook[] = items.map(item => ({
    id: item.id,
    title: item.text ? (item.text.length > 50 ? item.text.substring(0, 50) + '...' : item.text) : 'Untitled Runbook',
    description: item.metadata?.description || 'No description available',
    issueType: item.metadata?.issueType || 'General',
    rootCause: item.metadata?.rootCause || 'Unknown',
    resolutionSteps: item.metadata?.resolutionSteps || 'No steps provided',
    notes: item.metadata?.notes || '',
    category: item.metadata?.category || 'General',
    difficulty: item.metadata?.difficulty || 'Intermediate',
    tags: item.metadata?.tags || [],
    lastUsed: item.metadata?.lastUsed || new Date().toISOString().split('T')[0],
    successRate: item.metadata?.successRate || 85,
    author: item.metadata?.author || 'System',
    agent: item.metadata?.agent || 'CSVIngestionBee',
    type: item.metadata?.type || 'runbook',
    embeddings: item.metadata?.embeddings
  }));

  const filteredRunbooks = runbooks.filter(runbook => {
    const matchesSearch = runbook.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         runbook.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         runbook.issueType.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         runbook.resolutionSteps.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         runbook.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAgent = selectedAgent === 'All' || runbook.agent === selectedAgent;
    const matchesType = selectedType === 'All' || runbook.type === selectedType;
    
    return matchesSearch && matchesAgent && matchesType;
  });

  const sortedRunbooks = [...filteredRunbooks].sort((a, b) => {
    let aValue: any = a[sortBy];
    let bValue: any = b[sortBy];
    
    if (sortBy === 'lastUsed') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-orange-100 text-orange-800';
      case 'Expert': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await fetch('/api/runbooks/upload', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        setShowUploadModal(false);
        setCsvFile(null);
        onRefresh();
      } else {
        console.error('Failed to upload CSV file');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
    }
  };

  const executeRunbook = async (runbook: KnowledgeRunbook) => {
    try {
      const response = await fetch('/api/runbooks/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          runbookId: runbook.id,
          issueType: runbook.issueType,
          resolutionSteps: runbook.resolutionSteps
        })
      });
      
      if (response.ok) {
        console.log('Runbook executed successfully');
      } else {
        console.error('Failed to execute runbook');
      }
    } catch (error) {
      console.error('Error executing runbook:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={onRefresh} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <Book className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-semibold">Runbooks ({sortedRunbooks.length})</h2>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
            size="sm"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CSV
          </Button>
          <Button onClick={onRefresh} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <select 
          value={sortBy} 
          onChange={(e) => setSortBy(e.target.value as any)}
          className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
        >
          <option value="title">Title</option>
          <option value="lastUsed">Last Used</option>
          <option value="successRate">Success Rate</option>
          <option value="difficulty">Difficulty</option>
        </select>

        <select 
          value={sortOrder} 
          onChange={(e) => setSortOrder(e.target.value as any)}
          className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>

      {sortedRunbooks.length === 0 ? (
        <div className="text-center py-12">
          <Book className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No runbooks found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || selectedAgent !== 'All' || selectedType !== 'All'
              ? 'Try adjusting your search criteria or filters.'
              : 'Upload a CSV file to get started with runbooks.'}
          </p>
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="outline"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Runbooks CSV
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedRunbooks.map((runbook) => (
            <Card key={runbook.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-1">{runbook.title}</CardTitle>
                    <CardDescription className="text-sm">
                      {runbook.description}
                    </CardDescription>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button
                      onClick={() => executeRunbook(runbook)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => onDelete(runbook.id.toString())}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Issue Type</p>
                    <Badge variant="info" className="text-xs">
                      {runbook.issueType}
                    </Badge>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Root Cause</p>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {runbook.rootCause}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Resolution Steps</p>
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {runbook.resolutionSteps}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <Badge className={getDifficultyColor(runbook.difficulty)}>
                        {runbook.difficulty}
                      </Badge>
                      <span className={`text-sm font-medium ${getSuccessRateColor(runbook.successRate)}`}>
                        {runbook.successRate}%
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {runbook.lastUsed}
                    </div>
                  </div>
                  
                  {runbook.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-2">
                      {runbook.tags.slice(0, 3).map((tag, index) => (
                        <Badge key={index} variant="default" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {runbook.tags.length > 3 && (
                        <Badge variant="default" className="text-xs">
                          +{runbook.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Upload Runbooks CSV</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="text-sm text-gray-600">
                <p className="mb-2">CSV should contain columns:</p>
                <ul className="list-disc list-inside text-xs space-y-1">
                  <li>issue_type</li>
                  <li>root_cause</li>
                  <li>resolution_steps</li>
                  <li>notes</li>
                </ul>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  onClick={() => {
                    setShowUploadModal(false);
                    setCsvFile(null);
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCsvUpload}
                  disabled={!csvFile}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunbooksTab;
